<template>
    <section class="relative py-20 text-white" :style="{ background: backgroundColor }">
        <img v-if="backgroundImage" class="z-0 absolute left-0 hidden lg:block w-full" :src="$asset(backgroundImage, 'public')" alt="" />

        <div class="relative z-10" :class="containerClass">
            <div class="grid md:grid-cols-2 gap-12">
                <!-- Linke Spalte: Text -->
                <div>
                    <h2 class="mb-6" :class="[titleClass, titleColor]">
                        <span v-html="renderTitle()"></span>
                    </h2>
                    <p
                        v-for="(description, index) in descriptions"
                        :key="index"
                        class="mb-6"
                        :class="[textColor, descriptionClass, { 'font-semibold': description.bold }, description.class || '']"
                    >
                        {{ description.text || description }}
                    </p>
                </div>

                <!-- Rechte Spalte: Accordion -->
                <div class="space-y-4">
                    <div v-for="(item, index) in localAccordionItems" :key="index" class="rounded-lg overflow-hidden" :style="{ backgroundColor: accordionBackgroundColor }">
                        <button class="w-full p-6 text-left flex justify-between items-center hover:bg-white/5 transition" @click="toggleAccordion(index)">
                            <div class="flex items-center gap-4">
                                <!-- Icon: image or CSS class -->
                                <img v-if="item.iconImage" :src="$asset(item.iconImage, 'public')" class="w-[56px] h-[56px] object-contain flex-shrink-0" :alt="item.title" />
                                <i v-else :class="[item.icon, accordionIconColor, 'text-5xl']"></i>
                                <span :class="[item.isOpen ? accordionActiveColor : accordionTextColor, 'font-semibold pr-1', accordionTitleClass]">
                                    {{ item.title }}
                                </span>
                            </div>
                            <svg
                                class="w-6 h-6 flex-shrink-0 transition-transform"
                                :class="[{ 'rotate-180': item.isOpen }, accordionTextColor]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        <div v-show="item.isOpen" class="px-6 pb-6 font-pro text-[18px]" :class="accordionTextColor">
                            {{ item.content }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
    backgroundColor: {
        type: String,
        default: 'linear-gradient(to bottom right, #072551, #020E20)'
    },
    title: {
        type: String,
        default: ''
    },
    titleHighlight: {
        type: String,
        default: ''
    },
    titleHighlightGradientFrom: {
        type: String,
        default: '#11D2D4'
    },
    titleHighlightGradientTo: {
        type: String,
        default: '#0C7475'
    },
    titleColor: {
        type: String,
        default: 'text-white'
    },
    descriptions: {
        type: Array,
        default: () => []
    },
    textColor: {
        type: String,
        default: 'text-white'
    },
    accordionItems: {
        type: Array,
        default: () => []
    },
    accordionBackgroundColor: {
        type: String,
        default: '#FFFFFF'
    },
    accordionTextColor: {
        type: String,
        default: 'text-black'
    },
    accordionIconColor: {
        type: String,
        default: 'text-blue'
    },
    accordionActiveColor: {
        type: String,
        default: 'text-blue'
    },
    backgroundImage: {
        type: String,
        default: ''
    },
    titleClass: {
        type: String,
        default: 'text-3xl md:text-4xl font-bold'
    },
    descriptionClass: {
        type: String,
        default: ''
    },
    accordionTitleClass: {
        type: String,
        default: 'text-lg'
    },
    containerClass: {
        type: String,
        default: 'container mx-auto px-4 max-w-6xl'
    }
});

const localAccordionItems = ref([...props.accordionItems]);

watch(
    () => props.accordionItems,
    (newItems) => {
        localAccordionItems.value = [...newItems];
    },
    { deep: true }
);

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const highlightRegex = new RegExp(`(${props.titleHighlight})`, 'g');

    return props.title.replace(
        highlightRegex,
        `<span class="bg-gradient-to-tr text-transparent bg-clip-text" style="background: linear-gradient(to top right, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">$1</span>`
    );
};

const toggleAccordion = (index) => {
    localAccordionItems.value[index].isOpen = !localAccordionItems.value[index].isOpen;
};
</script>
